import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';

import { createTokenRefreshLink } from '@/adapters/auth/createTokenRefreshLink';
import { TOKEN_KEY } from '@/adapters/auth/storageKeys';
import { createTokenRefreshManager } from '@/adapters/auth/tokenRefreshManager';
import { BFF_BASE_URL } from '@/adapters/config/env';
import { cacheTypePolicies } from '@/adapters/graphql/cacheTypePolicies';
import { createErrorLink } from '@/adapters/graphql/createErrorLink';
import { createRefreshTokenExchanger } from '@/adapters/graphql/createRefreshTokenExchanger';
import { sentryLogger } from '@/adapters/logger/sentryLogger';
import { storage } from '@/adapters/storage/secureStorage';

// Aligns query error handling with the web apps (adminweb/userweb): partial
// data plus field-level errors render instead of discarding the whole query.
// Apollo v4 requires declaring default options for type safety.
declare module '@apollo/client' {
  namespace ApolloClient {
    namespace DeclareDefaultOptions {
      interface WatchQuery {
        errorPolicy: 'all';
      }
      interface Query {
        errorPolicy: 'all';
      }
    }
  }
}

const graphqlUri = `${BFF_BASE_URL}/graphql`;

const fetchWithTimeout = ((uri: RequestInfo | URL, options?: RequestInit) =>
  fetch(uri, {
    ...options,
    signal: options?.signal ?? AbortSignal.timeout(15_000),
  })) as typeof fetch;

const httpLink = new HttpLink({ uri: graphqlUri, fetch: fetchWithTimeout });

const authLink = setContext(async (_, { headers }) => {
  const token = await storage.getItem(TOKEN_KEY);

  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

const refreshHttpLink = new HttpLink({ uri: graphqlUri, fetch: fetchWithTimeout });

// Declaring default options above makes them required on every client. The
// refresh client only runs the token-exchange mutation, which ignores them.
const defaultOptions = {
  watchQuery: { errorPolicy: 'all' },
  query: { errorPolicy: 'all' },
} as const;

const refreshClient = new ApolloClient({
  link: refreshHttpLink,
  cache: new InMemoryCache(),
  defaultOptions,
});

const logoutHandlers: (() => void)[] = [];

const registerLogoutHandler = (handler: () => void) => {
  logoutHandlers.push(handler);
};

const tokenRefreshManager = createTokenRefreshManager({
  secureStore: storage,
  exchangeRefreshToken: createRefreshTokenExchanger(refreshClient),
  onLogout: () => logoutHandlers.forEach((handler) => handler()),
  logger: sentryLogger,
});

const tokenRefreshLink = createTokenRefreshLink(tokenRefreshManager);
const errorLink = createErrorLink(sentryLogger);

const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, tokenRefreshLink, authLink, httpLink]),
  cache: new InMemoryCache({ typePolicies: cacheTypePolicies }),
  // Mutations keep the default 'none' so mutation errors still reject.
  defaultOptions,
});

export { apolloClient, registerLogoutHandler };
